import React, { useEffect, useState } from "react";
import { ReactComponent as EditIcon } from "../../Global Icons/Edit.svg";
import { ReactComponent as DeleteIcon } from "../../Global Icons/Delete.svg";
import { ReactComponent as CloseIcon } from "../../Global Icons/CancelButton.svg";
import TextInput from "../../Global Functions/textInput";
import SelectInput from "../../Global Functions/selectInput";
import { useDispatch, useSelector } from "react-redux";
import {
  addVR,
  removeVr,
  addThreeSixtyPrevVideo,
} from "../../Redux/Actions/rentalPropertiesActions";
import { viewPropertyRental } from "../../Redux/Actions/rentalPropertiesActions";
import toast from "react-hot-toast";
import AddImage from "../../Global Functions/addImage";
import "./addVr.css";

const AddVr = ({ vrData, onVrRemoved, onClose }) => {
  const [formData, setFormData] = useState({
    configuration: "",
    configuration_id: "",
    iframe_url: "",
    is_user_vr: true,
  });

  // ✅ urlIframe.three_sixty is seeded from Redux state (localProperty),
  //    so already-added VR tours always reflect what's in the DB.
  const [urlIframe, setUrlIframe] = useState({
    property_id: vrData?.data?._id || "",
    three_sixty: [],
  });

  const [activeTab, setActiveTab] = useState("existing");
  const [previewVideoPreview, setPreviewVideoPreview] = useState("");
  const [editingIndex, setEditingIndex] = useState(null);
  const [otherPopup, setOtherPopup] = useState(false);
  const [videoUploading, setVideoUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadedVideoUrl, setUploadedVideoUrl] = useState("");

  const dispatch = useDispatch();

  // ─── Read fresh property from Redux state ────────────────────────────────
  // ✅ viewProperty(id) is dispatched on mount; Redux keeps the latest DB state.
  //    Components read from here so the list is always up-to-date.
  const { propertyRentalDetail, loading: propertyLoading } = useSelector(
    (state) => state.propertyRental || {}
  );
  const localProperty = propertyRentalDetail?.data;

  // ─── Fetch full property on mount ────────────────────────────────────────
  useEffect(() => {
    const propertyId = vrData?.data?._id;
    if (propertyId) {
      dispatch(viewPropertyRental(propertyId));
    }
  }, [vrData?.data?._id, dispatch]);


  useEffect(() => {
    if (!localProperty || localProperty._id !== vrData?.data?._id) return;

    setUrlIframe({
      property_id: localProperty._id,
      three_sixty: Array.isArray(localProperty.three_sixty)
        ? localProperty.three_sixty
        : [],
    });

    const videoUrl = localProperty.three_sixty_prev_video;
    setPreviewVideoPreview(videoUrl || "");
    setUploadedVideoUrl("");
  }, [localProperty, vrData?.data?._id]);


  const unitOptions = [
    ...(Array.isArray(localProperty?.units)
      ? localProperty.units.map((unit) => ({ value: unit?._id, label: unit?.name }))
      : []),
    { value: "others", label: "Other's" },
  ];

  // ─── Preview video upload ─────────────────────────────────────────────────
  const handlePreviewVideoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("video/")) return toast.error("Please select a valid video file!");

    setPreviewVideoPreview(URL.createObjectURL(file));
    setUploadedVideoUrl("");
    setVideoUploading(true);
    setUploadProgress(0);

    const progressInterval = setInterval(() => {
      setUploadProgress((prev) => {
        if (prev >= 90) { clearInterval(progressInterval); return 90; }
        return prev + 10;
      });
    }, 300);

    try {
      const responseUrl = await AddImage(file);
      clearInterval(progressInterval);
      setUploadProgress(100);
      if (responseUrl) {
        setUploadedVideoUrl(responseUrl);
        setPreviewVideoPreview(responseUrl);
        toast.success("Video uploaded! Click Save to confirm.");
      }
    } catch {
      clearInterval(progressInterval);
      toast.error("Video upload failed!");
      setPreviewVideoPreview("");
    } finally {
      setVideoUploading(false);
      setTimeout(() => setUploadProgress(0), 1000);
      e.target.value = "";
    }
  };

  const handleRemoveVideo = () => {
    setUploadedVideoUrl("");
    setPreviewVideoPreview("");
  };

  // ✅ Sends property_id + three_sixty_prev_video (the uploaded URL)
  const handlePreviewVideoSubmit = async () => {
    const finalUrl = uploadedVideoUrl || localProperty?.three_sixty_prev_video;
    if (!finalUrl) return toast.error("Please upload a preview video first!");

    try {
      const payload = new FormData();
      payload.append("property_id", localProperty?._id);
      payload.append("three_sixty_prev_video", finalUrl);

      const response = await dispatch(addThreeSixtyPrevVideo(payload));
      if (response?.statusCode === 200) {
        // ✅ Re-fetch so Redux + local state reflect the saved video
        dispatch(viewPropertyRental(localProperty._id));
        setUploadedVideoUrl("");
        if (typeof onVrRemoved === "function") onVrRemoved();
      }
    } catch {
      toast.error("Failed to save preview video");
    }
  };

  // ─── VR list: add / edit locally ─────────────────────────────────────────
  const handleAddEntry = (event) => {
    event.preventDefault();
    const { configuration, configuration_id, iframe_url, is_user_vr } = formData;
    if (!configuration || !configuration_id || !iframe_url) {
      return toast.error("All fields are required!");
    }

    if (editingIndex !== null) {
      setUrlIframe((prev) => ({
        ...prev,
        three_sixty: prev.three_sixty.map((item, i) =>
          i === editingIndex
            ? { ...item, configuration, configuration_id, iframe_url, is_user_vr }
            : item
        ),
      }));
      toast.success("VR Tour updated!");
      setEditingIndex(null);
    } else {
      setUrlIframe((prev) => ({
        ...prev,
        three_sixty: [
          ...prev.three_sixty,
          { configuration, configuration_id, iframe_url, is_user_vr, key_saved: false },
        ],
      }));
      toast.success("VR Tour added!");
    }

    setFormData({ configuration: "", configuration_id: "", iframe_url: "", is_user_vr: true });
    setOtherPopup(false);
  };

  const handleEdit = (index) => {
    const vrToEdit = urlIframe.three_sixty[index];
    setFormData({
      configuration: vrToEdit.configuration,
      configuration_id: vrToEdit.configuration_id,
      iframe_url: vrToEdit.iframe_url,
      is_user_vr: vrToEdit.is_user_vr,
    });
    setEditingIndex(index);
    setOtherPopup(vrToEdit.configuration_id === "others");
    setTimeout(() => {
      document.querySelector(".vr-form-section")?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }, 100);
  };

  const handleCancelEdit = () => {
    setEditingIndex(null);
    setOtherPopup(false);
    setFormData({ configuration: "", configuration_id: "", iframe_url: "", is_user_vr: true });
  };

  // ✅ Submit all: sends property_id + three_sixty array to addVR action
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (urlIframe.three_sixty.length === 0) return toast.error("Please add a link first!");
    try {
      const response = await dispatch(addVR(urlIframe));
      if (response?.statusCode === 200) {
        // ✅ Re-fetch from DB so Redux + local state are fresh for next open
        dispatch(viewPropertyRental(urlIframe.property_id));
        if (typeof onVrRemoved === "function") onVrRemoved();
        onClose?.();
      }
    } catch {
      toast.error("Failed to submit VR Tours");
    }
  };

  const handleRemove = async (indexToRemove) => {
    const vrToRemove = urlIframe.three_sixty[indexToRemove];

    if (!vrToRemove?._id) {
      // ✅ Unsaved (temp) item — remove locally only
      setUrlIframe((prev) => ({
        ...prev,
        three_sixty: prev.three_sixty.filter((_, i) => i !== indexToRemove),
      }));
      toast.success("VR Tour removed!");
      return;
    }

    try {
      // ✅ Saved item — call removeVr, then re-fetch to sync Redux state
      const response = await dispatch(
        removeVr({ property_id: urlIframe.property_id, three_sixty_id: vrToRemove._id })
      );
      if (response?.statusCode === 200) {
        dispatch(viewPropertyRental(urlIframe.property_id));
        toast.success("VR Tour deleted!");
        if (typeof onVrRemoved === "function") onVrRemoved();
      } else {
        toast.error("Failed to remove VR");
      }
    } catch {
      toast.error("Error removing VR");
    }
  };

  const handleClearAll = () => {
    setEditingIndex(null);
    setOtherPopup(false);
    setFormData({ configuration: "", configuration_id: "", iframe_url: "", is_user_vr: true });
    onClose?.();
  };

  // ✅ Show loading spinner while the first fetch is in progress
  const isFetching = propertyLoading && !localProperty;

  return (
    <div className="vr-modal-containers">
      <div className="vr-modal-headers">
        <h2 className="vr-modal-title">Add 360° / VR Experience</h2>
        <CloseIcon className="vr-close-icon" onClick={handleClearAll} title="Close" />
      </div>

      <div className="vr-tab-switcher">
        <button
          type="button"
          className={`vr-tab-btn ${activeTab === "existing" ? "active" : ""}`}
          onClick={() => setActiveTab("existing")}
        >
          Add VR
        </button>
        <button
          type="button"
          className={`vr-tab-btn ${activeTab === "preview" ? "active" : ""}`}
          onClick={() => setActiveTab("preview")}
        >
          Upload VR Prev Video
        </button>
      </div>

      <div className="vr-modal-content">
        {isFetching ? (
          <div className="vr-loading-state">
            <div className="vr-loading-spinner" />
            <span style={{ fontSize: "13px", fontWeight: 600 }}>Loading property data...</span>
          </div>
        ) : activeTab === "existing" ? (
          <>
            {/* ✅ Empty state — shown when no VR tours exist in DB yet */}
            {urlIframe.three_sixty.length === 0 && (
              <div className="vr-empty-state">
                <div className="vr-empty-icon">🥽</div>
                <p className="vr-empty-title">No VR Tours Added Yet</p>
                <p className="vr-empty-subtitle">Add your first 360° / VR tour using the form below</p>
              </div>
            )}

            {/* ✅ List of already-added VR tours (seeded from Redux → localProperty.three_sixty) */}
            {urlIframe.three_sixty.length > 0 && (
              <div className="vr-list-section">
                <h3 className="vr-list-heading">Added VR Tours</h3>
                <div className="vr-list-container">
                  {urlIframe.three_sixty.map((data, index) => (
                    <div
                      key={data._id || data.configuration_id || index}
                      className={`vr-list-item ${editingIndex === index ? "vr-editing" : ""}`}
                    >
                      <div className="vr-item-content">
                        <h4 className="vr-item-title">{data.configuration}</h4>
                        <p className="vr-item-url">{data.iframe_url}</p>
                        <div className="vr-visibility-status">
                          {data.is_user_vr
                            ? <span className="status-public">Visible to All</span>
                            : <span className="status-private">Agent only</span>}
                        </div>
                      </div>
                      <div className="vr-item-actions">
                        <button
                          type="button"
                          className="vr-action-btn vr-edit-btn"
                          onClick={() => handleEdit(index)}
                        >
                          <EditIcon />
                        </button>
                        <button
                          type="button"
                          className="vr-action-btn vr-delete-btn"
                          onClick={() => handleRemove(index)}
                        >
                          <DeleteIcon />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="vr-form-section">
              <h3 className="vr-form-heading">
                {editingIndex !== null
                  ? "Edit VR Tour"
                  : urlIframe.three_sixty.length > 0
                    ? "Add Another"
                    : "Add New VR Tour"}
              </h3>
              <div className="vr-form-grid">
                <SelectInput
                  label="Configuration"
                  disabled={otherPopup}
                  required={true}
                  value={formData.configuration_id}
                  placeholder="Select an Option"
                  options={unitOptions}
                  onChange={(e) => {
                    if (e.target.value === "others") {
                      setOtherPopup(true);
                      setFormData((prev) => ({ ...prev, configuration_id: "others", configuration: "" }));
                    } else {
                      setOtherPopup(false);
                      const selected = localProperty?.units?.find((u) => u._id === e.target.value);
                      setFormData((prev) => ({
                        ...prev,
                        configuration_id: selected?._id || "",
                        configuration: selected?.name || "",
                      }));
                    }
                  }}
                />

                {formData.configuration_id === "others" && (
                  <TextInput
                    label="Title"
                    required={true}
                    value={formData.configuration}
                    onChange={(e) => setFormData((prev) => ({ ...prev, configuration: e.target.value }))}
                  />
                )}

                <div className="vr-url-input-wrapper">
                  <TextInput
                    label="Iframe / URL"
                    required={true}
                    value={formData.iframe_url}
                    onChange={(e) => setFormData((prev) => ({ ...prev, iframe_url: e.target.value }))}
                  />
                  {formData.iframe_url.trim().length > 0 && (
                    <button type="button" className="vr-add-btn" onClick={handleAddEntry}>
                      {editingIndex !== null ? "Update" : "Add"}
                    </button>
                  )}
                </div>

                <div className="vr-checkbox-wrapper">
                  <label className="vr-checkbox-label">
                    <input
                      type="checkbox"
                      className="vr-checkbox-input"
                      checked={formData.is_user_vr}
                      onChange={(e) => setFormData((prev) => ({ ...prev, is_user_vr: e.target.checked }))}
                    />
                    <span className="vr-checkbox-text">
                      User Available
                      {!formData.is_user_vr && <span className="disclaimer-text">(VR hidden from users)</span>}
                    </span>
                  </label>
                </div>

                {editingIndex !== null && (
                  <button type="button" className="vr-cancel-edit-btn" onClick={handleCancelEdit}>
                    Cancel Edit
                  </button>
                )}
              </div>
            </div>

            {urlIframe.three_sixty.length > 0 && (
              <div className="vr-submit-section">
                <button type="button" className="vr-submit-btn" onClick={handleSubmit}>
                  Submit All
                </button>
              </div>
            )}
          </>
        ) : (
          /* ─── Preview Video Tab ──────────────────────────────────────────── */
          <div className="vr-form-section">
            <h3 className="vr-form-heading">Upload Preview Video</h3>
            <div className="vr-form-grid">
              <div className="vr-upload-field">
                <label className="vr-upload-label">Preview Video</label>
                <input
                  type="file"
                  accept="video/*"
                  id="vr-preview-video-upload"
                  style={{ display: "none" }}
                  onChange={handlePreviewVideoChange}
                  disabled={videoUploading}
                />
                <label
                  htmlFor="vr-preview-video-upload"
                  className="label-class-images"
                  style={{ cursor: videoUploading ? "not-allowed" : "pointer" }}
                >
                  {videoUploading ? `Uploading... ${uploadProgress}%` : "Click to Upload Video"}
                </label>
              </div>

              {videoUploading && (
                <div style={{ height: "6px", backgroundColor: "#e2e8f0", borderRadius: "99px", overflow: "hidden" }}>
                  <div style={{
                    width: `${uploadProgress}%`,
                    height: "100%",
                    backgroundColor: uploadProgress === 100 ? "#22c55e" : "#3b82f6",
                    transition: "width 0.3s ease",
                    borderRadius: "99px",
                  }} />
                </div>
              )}

              {/* ✅ Shows existing DB video OR newly uploaded preview */}
              {previewVideoPreview && !videoUploading && (
                <div style={{ position: "relative" }}>
                  <video
                    className="vr-video-preview"
                    controls
                    src={previewVideoPreview}
                    style={{ borderRadius: "10px", width: "100%" }}
                  />
                  <button
                    type="button"
                    onClick={handleRemoveVideo}
                    style={{
                      position: "absolute", top: "8px", right: "8px",
                      width: "28px", height: "28px",
                      backgroundColor: "rgba(0,0,0,0.6)", color: "white",
                      border: "none", borderRadius: "50%", cursor: "pointer",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: "16px", fontWeight: "bold",
                    }}
                  >×</button>
                </div>
              )}

              <div className="vr-submit-section">
                <button
                  type="button"
                  className="vr-submit-btn"
                  onClick={handlePreviewVideoSubmit}
                  disabled={videoUploading || (!uploadedVideoUrl && !localProperty?.three_sixty_prev_video)}
                >
                  {/* ✅ Label changes based on whether a video already exists in DB */}
                  {localProperty?.three_sixty_prev_video ? "Update Preview Video" : "Upload Preview Video"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AddVr;